/**
 * The two locales the platform speaks, and the direction each reads in.
 *
 * Arabic is the default, not English: this is a Gulf-market storefront, so right-to-left is the
 * primary case and the one everything is designed around.
 */
export const LOCALES = ['ar', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'ar';

export type Direction = 'rtl' | 'ltr';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Falls back to the default rather than throwing — an unknown locale is not an error. */
export function normaliseLocale(value: unknown): Locale {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}

export function directionOf(locale: Locale): Direction {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/** The `lang` and `dir` attributes for a document root, so the pair can never disagree. */
export function documentAttributes(locale: Locale): { lang: Locale; dir: Direction } {
  return { lang: locale, dir: directionOf(locale) };
}

/** The other locale, for the language switch. */
export function alternateLocale(locale: Locale): Locale {
  return locale === 'ar' ? 'en' : 'ar';
}

/** How each language names itself. Never translated — a switcher shows each in its own script. */
export const LOCALE_NAMES: Record<Locale, string> = {
  ar: 'العربية',
  en: 'English',
};
